import { type FC, type PropsWithChildren, useCallback, useMemo } from 'react';

import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { Button } from '@vakantiesnl/components/src/atoms/Button';
import { Typography } from '@vakantiesnl/components/src/atoms/Typography';
import { useBreakpoints } from '@vakantiesnl/components/src/utils/useBreakpoints';
import { useMicroCopyContext } from '@vakantiesnl/services/src/context/microCopyContext';
import { inject } from '@vakantiesnl/services/src/util/template';

import { useStyles } from './FiltersWrapper.style';

interface Props {
	showDialog: boolean;
	toggleDialog: (a: boolean) => void;
	total?: number;
	isLoading: boolean;
}

export const FiltersWrapper: FC<PropsWithChildren<Props>> = ({
	children,
	showDialog,
	toggleDialog,
	total,
	isLoading,
}) => {
	const { isDesktop } = useBreakpoints();
	const { classes, cx } = useStyles();
	const microCopies = useMicroCopyContext();

	const closeModal = useCallback(() => {
		toggleDialog(false);
	}, [toggleDialog]);

	/** A closed modal should scroll to the top for a new search request */
	const closeModalWithScroll = useCallback(() => {
		closeModal();

		window.scrollTo({ top: 0, behavior: 'smooth' });
	}, [closeModal]);

	const formattedTotal = total?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');

	const resultsButtonText = useMemo(() => {
		if (isLoading) {
			return microCopies['common.fetchingResults'];
		}
		if (total && total > 1) {
			return inject(microCopies['common.showResults'], {
				total: formattedTotal || '0',
			});
		} else if (total === 1) {
			return microCopies['common.showOneResult'];
		} else {
			return inject(microCopies['common.showResults'], {
				total: '0',
			});
		}
	}, [microCopies, formattedTotal, total, isLoading]);

	return (
		<>
			{!isDesktop ? (
				<Dialog
					fullWidth={true}
					maxWidth="md"
					fullScreen={true}
					open={showDialog}
					onClose={closeModal}
					aria-labelledby="dialog-title"
					data-qa="filter-modal"
					className={classes.dialog}
				>
					<DialogTitle className={classes.dialogTitle}>
						<Typography className={classes.counter} as="span" variant="headingMd">
							{microCopies['common.filtersOpClean']}:
						</Typography>
						<Button
							onClick={closeModal}
							data-qa="button-close"
							aria-label={microCopies['closeText']}
							variant="tertiary"
							leadingIcon="cancel"
						/>
					</DialogTitle>
					<DialogContent className={cx(classes.dialogContent)}>{children}</DialogContent>
					<DialogActions className={classes.dialogActions}>
						<Button
							size="small"
							className={classes.resultsButton}
							aria-label="Show results"
							onClick={closeModalWithScroll}
							disabled={isLoading}
						>
							{resultsButtonText}
						</Button>
					</DialogActions>
				</Dialog>
			) : (
				<div className={classes.container}>{children}</div>
			)}
		</>
	);
};
